import React, { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge, EmptyState } from "@/components/Common";
import { toast } from "sonner";
import { Download, Plus } from "lucide-react";

/**
 * Records tab — daily attendance with date selection applied to the table,
 * the summary chips and the CSV export.
 */
export default function AttendanceRecords({ isManager, branches, refreshKey, onChanged }) {
  // Tenant-local date (Africa/Kampala), not UTC — avoids off-by-one late at night
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Africa/Kampala" });
  const [range, setRange] = useState({ from: today, to: today });
  const [applied, setApplied] = useState({ from: today, to: today });
  const [filters, setFilters] = useState({ branch: "", status: "" });
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [manualOpen, setManualOpen] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [manual, setManual] = useState({ staff_id: "", date: today, check_in: "", check_out: "", note: "" });
  const [manualBusy, setManualBusy] = useState(false);

  const qs = () => {
    const p = new URLSearchParams();
    if (applied.from) p.append("from_date", applied.from);
    if (applied.to) p.append("to_date", applied.to);
    if (filters.branch) p.append("branch", filters.branch);
    if (filters.status) p.append("status", filters.status);
    return p.toString();
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [r, s] = await Promise.all([
          api.get(`/attendance/records?${qs()}`),
          api.get(`/attendance/summary?${qs()}`),
        ]);
        setItems(r.data);
        setSummary(s.data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, [applied, filters, refreshKey]);

  useEffect(() => {
    if (manualOpen && staffList.length === 0) {
      api.get("/attendance/enrollments").then(({ data }) => setStaffList(data)).catch(() => {});
    }
  }, [manualOpen]);

  const setPreset = (days) => {
    const to = new Date().toLocaleDateString("en-CA", { timeZone: "Africa/Kampala" });
    const d = new Date(); d.setDate(d.getDate() - days + 1);
    const from = d.toLocaleDateString("en-CA", { timeZone: "Africa/Kampala" });
    setRange({ from, to });
    setApplied({ from, to });
  };

  const saveManual = async () => {
    if (!manual.staff_id || !manual.date) { toast.error("Pick a staff member and date"); return; }
    setManualBusy(true);
    try {
      await api.post("/attendance/manual", manual);
      toast.success("Attendance saved");
      setManualOpen(false);
      setApplied((a) => ({ ...a }));
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setManualBusy(false); }
  };

  return (
    <div>
      {/* Date selection — applies to table, summary and export */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-wrap items-end gap-3"
        data-testid="att-range-bar">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">From</div>
          <Input type="date" value={range.from} data-testid="att-from-date"
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="h-9 w-40" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">To</div>
          <Input type="date" value={range.to} data-testid="att-to-date"
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="h-9 w-40" />
        </div>
        <Button size="sm" onClick={() => setApplied({ ...range })} data-testid="att-apply-range"
          className="bg-emerald-900 hover:bg-emerald-800">Apply</Button>
        <button onClick={() => setPreset(1)} className="px-3 h-9 rounded-full border border-gray-300 text-xs text-gray-600 hover:border-emerald-700" data-testid="att-preset-today">Today</button>
        <button onClick={() => setPreset(7)} className="px-3 h-9 rounded-full border border-gray-300 text-xs text-gray-600 hover:border-emerald-700" data-testid="att-preset-7d">Last 7d</button>
        <button onClick={() => setPreset(30)} className="px-3 h-9 rounded-full border border-gray-300 text-xs text-gray-600 hover:border-emerald-700" data-testid="att-preset-30d">Last 30d</button>
        {isManager && (
          <select value={filters.branch} data-testid="att-filter-branch"
            onChange={(e) => setFilters((f) => ({ ...f, branch: e.target.value }))}
            className="h-9 rounded-md border border-gray-300 bg-white px-2.5 text-sm">
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
        )}
        <select value={filters.status} data-testid="att-filter-status"
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="h-9 rounded-md border border-gray-300 bg-white px-2.5 text-sm">
          <option value="">Any status</option>
          <option value="present">Present (on time)</option>
          <option value="late">Late</option>
        </select>
        <div className="ml-auto flex gap-2">
          {isManager && (
            <Button variant="outline" size="sm" onClick={() => setManualOpen(true)} data-testid="att-manual-button">
              <Plus size={14} className="mr-1.5" /> Manual entry
            </Button>
          )}
          <a href={`${API_BASE}/attendance/records.csv?${qs()}`} data-testid="att-export-csv"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-gray-300 text-xs font-medium text-gray-700 hover:border-emerald-700 hover:text-emerald-800">
            <Download size={13} /> Export CSV
          </a>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="att-summary">
        <Badge tone="emerald">Present: {summary.present ?? 0}</Badge>
        <Badge tone="red">Late: {summary.late ?? 0} (after {summary.late_after})</Badge>
        <Badge tone="amber">Absent: {summary.absent ?? 0}</Badge>
        <Badge tone="slate">On leave: {summary.on_leave ?? 0}</Badge>
        <Badge tone="slate">Staff recorded: {summary.staff_recorded ?? 0} / {summary.active_staff ?? 0}</Badge>
      </div>

      {/* Records table */}
      {loading ? (
        <div className="text-sm text-gray-500 py-12 text-center">Loading records…</div>
      ) : items.length === 0 ? (
        <EmptyState title="No attendance records in this range"
          hint="Records appear when staff place a finger on the kiosk or a door device, or via manual entry." />
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto" data-testid="att-records-table">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th className="px-3 py-2.5">Date</th>
                <th className="px-3 py-2.5">Staff</th>
                <th className="px-3 py-2.5">Branch</th>
                <th className="px-3 py-2.5">Check in</th>
                <th className="px-3 py-2.5">Check out</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Method</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60"
                  data-testid={`att-row-${r.id}`}>
                  <td className="px-3 py-2.5 text-gray-600">{r.date}</td>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-gray-900">{r.staff_name}</div>
                    <div className="text-[11px] text-gray-400">{r.staff_number}</div>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600">{r.branch || "—"}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{r.check_in || "—"}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{r.check_out || "—"}</td>
                  <td className="px-3 py-2.5">
                    <Badge tone={r.status === "late" ? "red" : "emerald"}>{r.status}</Badge>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">
                    {r.method}{r.device_name ? ` · ${r.device_name}` : ""}
                    {r.note ? <div className="text-[11px] text-gray-400">{r.note}</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Manual entry dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="sm:max-w-md" data-testid="att-manual-dialog">
          <DialogHeader>
            <DialogTitle>Manual attendance entry</DialogTitle>
            <DialogDescription>Create or correct a day's record when a scanner was unavailable.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Staff member</Label>
              <select value={manual.staff_id} data-testid="att-manual-staff"
                onChange={(e) => setManual((m) => ({ ...m, staff_id: e.target.value }))}
                className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm">
                <option value="">Select staff…</option>
                {staffList.map((s) => (
                  <option key={s.staff_id} value={s.staff_id}>{s.name} ({s.staff_number})</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={manual.date} data-testid="att-manual-date"
                  onChange={(e) => setManual((m) => ({ ...m, date: e.target.value }))} className="mt-1 h-10" />
              </div>
              <div>
                <Label className="text-xs">Check in</Label>
                <Input type="time" value={manual.check_in} data-testid="att-manual-in"
                  onChange={(e) => setManual((m) => ({ ...m, check_in: e.target.value }))} className="mt-1 h-10" />
              </div>
              <div>
                <Label className="text-xs">Check out</Label>
                <Input type="time" value={manual.check_out} data-testid="att-manual-out"
                  onChange={(e) => setManual((m) => ({ ...m, check_out: e.target.value }))} className="mt-1 h-10" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Input value={manual.note} placeholder="e.g. scanner offline, verified by supervisor"
                onChange={(e) => setManual((m) => ({ ...m, note: e.target.value }))}
                className="mt-1 h-10" data-testid="att-manual-note" />
            </div>
            <Button onClick={saveManual} disabled={manualBusy}
              className="w-full h-11 bg-emerald-900 hover:bg-emerald-800" data-testid="att-manual-save">
              {manualBusy ? "Saving…" : "Save record"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
